import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSyncAlt, FaArrowUp, FaArrowDown, FaPhone, FaGift, FaCog, FaBan } from 'react-icons/fa';
import { MdDialpad, MdWorkspacePremium, MdCreditCardOff } from 'react-icons/md';
import { BsFillTelephonePlusFill } from 'react-icons/bs';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api, post_api } from '../../../redux/api_funcs';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import { get_api_url } from '../../../utils/urls';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import AudioPlayer from '../students/student_profile/CallAudioPlayer';
import Paginator from '../../util_components/paginator';
import MytNotification from '../../../myt_chat/notification';
import DialerModal from './DialerModal';
import RecentCallsFilters from './RecentCallsFilters';
import SettingsModal from './SettingsModal';
import { useResyncOnReconnect } from './resync_hooks';

const STATUS_STYLES = {
  completed: { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  'in-progress': { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  answered: { color: 'var(--state-success-text)', background: 'var(--state-success-bg)' },
  ringing: { color: 'var(--state-warning-text)', background: 'var(--state-warning-bg)' },
  initiated: { color: 'var(--state-warning-text)', background: 'var(--state-warning-bg)' },
  busy: { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  failed: { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  'no-answer': { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  'not-answered': { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  'no-response': { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' },
  canceled: { color: 'var(--text-secondary)', background: 'var(--border-default)' },
  // Both blocked outcomes render as one pill. The row's raw status still says
  // which of the two happened (blocked:message or blocked:rejected); the table
  // has no room to say it and nobody filters on the difference.
  blocked: { color: 'var(--state-error-text)', background: 'var(--state-error-bg)' }
};

// isMachinePickup: Twilio AMD flagged the callee leg as an answering machine
// or fax — the call "completed" but no human ever picked up.
const isMachinePickup = (answeredBy) =>
  !!answeredBy && (answeredBy.startsWith('machine') || answeredBy === 'fax');

function StatusPill({ status, answered, answeredBy }) {
  let display = status;
  if (status === 'completed' && !answered) {
    display = isMachinePickup(answeredBy) ? 'no-response' : 'not-answered';
  }
  // The server stores blocked:message and blocked:rejected; the calls-history
  // filter buckets both as 'blocked' (recentCallStatusExpr), so the pill has to
  // agree or a row reads under a label its own filter does not offer.
  if (typeof display === 'string' && display.startsWith('blocked:')) {
    display = 'blocked';
  }
  const style = STATUS_STYLES[display] ||
    STATUS_STYLES[status] || {
      color: 'var(--text-secondary)',
      background: 'var(--surface-sunken)'
    };
  return (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
        textTransform: 'capitalize'
      }}
    >
      {display || '—'}
    </span>
  );
}

// Page size for the calls-history table. The server accepts per_page up to
// 200 and defaults to 50; keep this in sync with the pager's "X–Y of Z" math.
const RECENT_CALLS_PER_PAGE = 50;

// pagerButtonStyle keeps the Prev/Next buttons visually consistent with the
// refresh button above the table (tokens only — no hardcoded colors).
const pagerButtonStyle = (disabled) => ({
  padding: '4px 12px',
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-card)',
  color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1
});

// titleCaseStatus turns a normalized status bucket from the server
// ('no-answer', 'not-answered', …) into a filter-dropdown label.
const titleCaseStatus = (s) => s.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());

const formatSeconds = (secs) => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// studentStatusBadge resolves the icon, color, and tooltip for a Recent Calls
// student-status indicator. The status string comes from the server-side
// CASE expression in listRecentCalls / findRecentCallRowByID (see db.go).
// Member > trial precedence is enforced server-side; this function just
// maps the resulting string to the visual.
//
// Returns null for the "no student match" case so the caller can omit the
// icon entirely without rendering a placeholder.
function studentStatusBadge(status) {
  switch (status) {
    case 'member':
      return { Icon: MdWorkspacePremium, color: 'var(--state-warning)', label: 'Paid member' };
    case 'trial':
      return { Icon: FaGift, color: 'var(--state-info)', label: 'Trial user' };
    case 'none':
      return { Icon: MdCreditCardOff, color: 'var(--text-muted)', label: 'Not on a plan' };
    default:
      return null;
  }
}

// Slow safety-net poll. The primary signal for ongoing-call mutations is the
// `ongoing_calls_changed` relay (see CallbackHandler subscription below); the
// poll only catches drift if a user's chat socket missed an event (transient
// disconnect, tab waking from background) or if some future code path forgets
// to fan out a relay. Random in [5, 10) min to spread server load across tabs.
const POLL_MIN_MS = 5 * 60 * 1000;
const POLL_MAX_MS = 10 * 60 * 1000;

const nextPollDelay = () => POLL_MIN_MS + Math.floor(Math.random() * (POLL_MAX_MS - POLL_MIN_MS));

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

const formatDuration = (startedAt, now) => {
  if (!startedAt) return '--:--';
  const start = new Date(startedAt).getTime();
  const diff = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

function TwilioAgent() {
  const dispatch = useDispatch();
  // care_uuid is a shared constant for the care chat user, not the signed-in
  // agent; identity must come from the auth token.
  const currentUserUUID = getAuthUserInfoOrDefault().uuid || '';

  // access_type is a comma-separated MySQL SET, so a user can hold SUPER_USER
  // alongside other roles — split and match the exact token. The gear button and
  // settings modal are super-user only (the goserver also enforces this).
  const accessType = useSelector((s) => s.home.access_type);
  const isSuperUser = (accessType || '')
    .split(',')
    .map((t) => t.trim().toUpperCase())
    .includes('SUPER_USER');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [ongoingCalls, setOngoingCalls] = useState([]);
  // Server-derived "the current user is currently in this call" — used to
  // keep the Ongoing Calls Join button consistent across tabs. The per-tab
  // window.__twilio*Active flags only know about the SDK Device that lives
  // in this tab; another tab opened by the same user wouldn't see them and
  // would otherwise show Join as available on a row the user is already in.
  const [yourActiveCallUUID, setYourActiveCallUUID] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);

  const defaultFrom = useMemo(() => moment().subtract(1, 'month').format('YYYY-MM-DD'), []);
  const defaultTo = useMemo(() => moment().format('YYYY-MM-DD'), []);
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  // recentCalls holds ONE PAGE of the calls-history table. Filtering, sorting
  // and paging are all server-side (GET /recent_calls) — the browser never
  // holds the whole date range, so nothing here may filter locally.
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Filter dropdown contents, computed by the server over the selected date
  // range. Previously derived from the fetched rows, which only worked while
  // the frontend held every row in the range.
  const [agentOptions, setAgentOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // Calls-history filters. Every one is sent to the server as a query param.
  // Empty arrays mean "no filter / show all"; the single-select toggles
  // (member status, direction) default to 'all'. Cleared together via
  // clearFilters below.
  const [memberStatusFilter, setMemberStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState([]);
  const [callStatusFilter, setCallStatusFilter] = useState([]);
  const [directionFilter, setDirectionFilter] = useState('all');

  const [notificationPermission, setNotificationPermission] = useState(() =>
    MytNotification.getPermissionState()
  );
  const [notificationBannerDismissed, setNotificationBannerDismissed] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);

  // Live participant set per call_uuid, fed by the `participant_changed`
  // relay. Used to disable the Join button on rows the current user is
  // already in. Pruned in a useEffect against `ongoingCalls` so the map
  // doesn't grow unbounded over a long-lived /twilio-agent session — see
  // the cleanup effect below.
  const [participantsByCallUUID, setParticipantsByCallUUID] = useState({});

  useEffect(() => {
    const handler = (item) => {
      if (!item || !item.data) return;
      const { type } = item.data;
      if (type === 'participant_changed' && item.data.call_uuid) {
        const participants = Array.isArray(item.data.participants) ? item.data.participants : [];
        setParticipantsByCallUUID((prev) => ({ ...prev, [item.data.call_uuid]: participants }));
        // If THIS user just left a call they were busy on, clear the
        // server-derived yourActiveCallUUID locally. The agentBusyKey is
        // already DEL'd server-side by the participant-leave webhook, but
        // the frontend's cached value would otherwise stay stale until
        // the next /ongoing_calls fetch — and `selfInCall` ORs in
        // `call.uuid === yourActiveCallUUID`, so the Join icon stays
        // hidden until refresh. This keeps the table reactive to leaves.
        if (currentUserUUID && !participants.includes(`cms_${currentUserUUID}`)) {
          setYourActiveCallUUID((prev) => (prev === item.data.call_uuid ? '' : prev));
        }
      } else if (type === 'unlocked' && item.data.call_uuid) {
        // Refresh the table so the row gains its conference_sid → Join icon.
        window.dispatchEvent(new CustomEvent('twilio-ongoing-calls-refresh'));
      } else if (type === 'ongoing_calls_changed') {
        // Server fans this out on every row create / terminal-state transition
        // so all ops users (not just the one driving the change) see new
        // calls appear and ended calls disappear without a manual refresh.
        window.dispatchEvent(new CustomEvent('twilio-ongoing-calls-refresh'));
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => CallbackHandler.shared.unregisterRelayItemListener(handler);
  }, []);

  // Prune the participants map whenever the ongoing-calls list refreshes.
  // Without this the map grows unbounded over a long /twilio-agent session
  // — every relay event for a now-ended call permanently leaves an entry
  // behind. We restrict the map to keys that are currently in
  // ongoingCalls. We use the functional setState shape and only return a
  // new object when something actually changed, so an unrelated re-render
  // doesn't spuriously break referential equality for downstream memos.
  useEffect(() => {
    if (ongoingCalls.length === 0) {
      setParticipantsByCallUUID((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    const liveUUIDs = new Set(ongoingCalls.map((c) => c.uuid).filter(Boolean));
    setParticipantsByCallUUID((prev) => {
      let changed = false;
      const next = {};
      for (const [uuid, list] of Object.entries(prev)) {
        if (liveUUIDs.has(uuid)) {
          next[uuid] = list;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [ongoingCalls]);

  const joinOngoing = useCallback(
    async (callUUID) => {
      if (!callUUID) return;
      // Stash the target so the next SDK 'incoming' event in
      // GlobalIncomingCallManager knows this is a conference dial-in
      // (not a fresh inbound PSTN), auto-accepts, and tags the pill
      // with this call's uuid. The Manager clears it as soon as it
      // consumes the value; we add a watchdog timeout to clear it if
      // no SDK incoming arrives at all (e.g., Twilio queued the call
      // but the joiner's Device isn't registered) — otherwise a stale
      // target would hijack the next genuine inbound PSTN call.
      //
      // ALSO: dispatch a `twilio-warm-audio` event synchronously from
      // inside this click handler so the manager can resume Twilio's
      // AudioContext while we're still in the browser's user-gesture
      // window. Without this the SDK's auto-accept (called later from
      // the device.on('incoming') callback, well after the gesture
      // window has closed) gets the WebRTC remote stream silently
      // muted by Chrome's autoplay policy — manifesting as "joiner
      // sees the pill but hears nothing."
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('twilio-warm-audio'));
        window.__twilioJoinTarget = callUUID;
        if (window.__twilioJoinTargetTimeout) {
          clearTimeout(window.__twilioJoinTargetTimeout);
        }
        window.__twilioJoinTargetTimeout = setTimeout(() => {
          if (window.__twilioJoinTarget === callUUID) {
            window.__twilioJoinTarget = null;
            dispatch(
              set_notification_variable(
                true,
                'error',
                'Did not connect to the call — your browser may not be reachable. Toggle yourself available and try again.'
              )
            );
          }
          window.__twilioJoinTargetTimeout = null;
        }, 30 * 1000);
      }
      try {
        await post_api(getUrl('/v2/twilio_call/join_ongoing'), { call_uuid: callUUID }, true);
      } catch (err) {
        // Clear the target so a stale value doesn't hijack a future inbound.
        if (typeof window !== 'undefined') {
          window.__twilioJoinTarget = null;
          if (window.__twilioJoinTargetTimeout) {
            clearTimeout(window.__twilioJoinTargetTimeout);
            window.__twilioJoinTargetTimeout = null;
          }
        }
        const reason = err?.response?.data?.reason || err?.message || 'Failed to join the call';
        dispatch(set_notification_variable(true, 'error', reason));
      }
    },
    [dispatch]
  );

  // The dialer modal hands the typed number off to the global outbound
  // call manager via a window event. The manager owns the Twilio Device
  // and pill so the call survives any nav out of /twilio-agent.
  const handleDialerCall = useCallback((number) => {
    window.dispatchEvent(
      new CustomEvent('twilio-outgoing-call-start', { detail: { toNumber: number } })
    );
    setDialerOpen(false);
  }, []);

  const refreshNotificationPermission = useCallback(() => {
    setNotificationPermission(MytNotification.getPermissionState());
  }, []);

  useEffect(() => {
    MytNotification.webPushInit();
    refreshNotificationPermission();

    const onVis = () => refreshNotificationPermission();
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshNotificationPermission]);

  const requestNotificationPermission = async () => {
    const perm = await MytNotification.ensurePermission({ userGesture: true });
    setNotificationPermission(perm);
    setNotificationBannerDismissed(false);
    if (perm === 'granted') {
      MytNotification.webPushInit();
    }
  };

  // Sorting is a server param now, so flipping it re-queries from page 1
  // rather than reordering the page already on screen (which would only sort
  // 50 of N rows).
  const toggleSort = () => {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const agentSelectOptions = useMemo(
    () => agentOptions.map((n) => ({ value: n, label: n })),
    [agentOptions]
  );

  const statusSelectOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s, label: titleCaseStatus(s) })),
    [statusOptions]
  );

  const activeFilterCount =
    (memberStatusFilter !== 'all' ? 1 : 0) +
    (agentFilter.length ? 1 : 0) +
    (callStatusFilter.length ? 1 : 0) +
    (directionFilter !== 'all' ? 1 : 0);

  const clearFilters = useCallback(() => {
    setMemberStatusFilter('all');
    setAgentFilter([]);
    setCallStatusFilter([]);
    setDirectionFilter('all');
    setPage(1);
  }, []);

  // Every filter change must reset to page 1: page 7 of the old result set is
  // usually out of range for the new one, and the server would clamp it to the
  // last page, which reads as a random jump.
  const changeMemberStatus = useCallback((v) => {
    setMemberStatusFilter(v);
    setPage(1);
  }, []);

  const changeAgents = useCallback((v) => {
    setAgentFilter(v);
    setPage(1);
  }, []);

  const changeStatuses = useCallback((v) => {
    setCallStatusFilter(v);
    setPage(1);
  }, []);

  const changeDirection = useCallback((v) => {
    setDirectionFilter(v);
    setPage(1);
  }, []);

  const changeDateRange = useCallback(({ from, to }) => {
    setFromDate(from);
    setToDate(to);
    setPage(1);
  }, []);

  // "X–Y of Z" for the pager. Z is the server's total for the current filter
  // set, so it counts the whole range and not just the page on screen.
  const rangeStart = totalCalls === 0 ? 0 : (page - 1) * RECENT_CALLS_PER_PAGE + 1;
  const rangeEnd = totalCalls === 0 ? 0 : rangeStart + recentCalls.length - 1;
  const resultCountLabel =
    totalCalls === 0
      ? 'No results'
      : `${rangeStart}–${rangeEnd} of ${totalCalls} result${totalCalls === 1 ? '' : 's'}`;

  const fetchRecentCalls = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const params = new URLSearchParams();
      params.set('from', fromDate);
      params.set('to', toDate);
      params.set('page', String(page));
      params.set('per_page', String(RECENT_CALLS_PER_PAGE));
      params.set('sort', sortDir);
      if (memberStatusFilter !== 'all') params.set('member_status', memberStatusFilter);
      if (directionFilter !== 'all') params.set('direction', directionFilter);
      agentFilter.forEach((a) => params.append('agents', a));
      callStatusFilter.forEach((s) => params.append('statuses', s));

      const resp = await get_api(
        `${getUrl('/v2/twilio_call/recent_calls')}?${params.toString()}`,
        true
      );
      const data = resp.data?.data || {};
      setRecentCalls(data.calls || []);
      setTotalCalls(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setAgentOptions(data.agent_options || []);
      setStatusOptions(data.status_options || []);
      // The server clamps a request past the last page (e.g. filters narrowed
      // while sitting on page 9); follow it so the pager highlights the page
      // actually rendered.
      if (data.page && data.page !== page) setPage(data.page);
    } catch (e) {
      setRecentError('Failed to load recent calls');
    } finally {
      setRecentLoading(false);
    }
  }, [
    fromDate,
    toDate,
    page,
    sortDir,
    memberStatusFilter,
    directionFilter,
    agentFilter,
    callStatusFilter
  ]);

  // Refetch whenever the date range changes (or on mount).
  useEffect(() => {
    fetchRecentCalls();
  }, [fetchRecentCalls]);

  const fetchOngoingCalls = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/ongoing_calls'), true);
      setOngoingCalls(resp.data?.data?.calls || []);
      setYourActiveCallUUID(resp.data?.data?.your_active_call_uuid || '');
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to fetch ongoing calls'));
    }
  }, []);

  const fetchAvailableAgents = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/available_agents'), true);
      setAvailableAgents(resp.data?.data?.agents || []);
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to fetch available agents'));
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setResyncing(true);
    try {
      await Promise.all([fetchOngoingCalls(), fetchAvailableAgents()]);
    } finally {
      setLoading(false);
      setResyncing(false);
    }
  }, [fetchOngoingCalls, fetchAvailableAgents]);

  useEffect(() => {
    let cancelled = false;
    fetchAll();

    const timers = [];
    const schedule = (fn) => {
      const run = async () => {
        if (cancelled) return;
        await fn();
        if (cancelled) return;
        timers.push(setTimeout(run, nextPollDelay()));
      };
      timers.push(setTimeout(run, nextPollDelay()));
    };
    schedule(fetchOngoingCalls);
    // Available agents list is push-driven via the
    // `twilio-agent-availability-changed` broadcast (see effect below) —
    // no polling needed.

    const tickId = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      clearInterval(tickId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh Ongoing calls immediately when an incoming call arrives or ends.
  // GlobalIncomingCallManager dispatches this on relay arrival, pill-accept,
  // disconnect, cancel, reject, or error. We also refetch 2 s later to catch
  // cases where Twilio's Status webhook reaches our backend slightly after
  // the local pill transition.
  useEffect(() => {
    const timers = [];
    const onRefresh = () => {
      fetchOngoingCalls();
      timers.push(setTimeout(() => fetchOngoingCalls(), 2000));
    };
    window.addEventListener('twilio-ongoing-calls-refresh', onRefresh);
    return () => {
      window.removeEventListener('twilio-ongoing-calls-refresh', onRefresh);
      timers.forEach(clearTimeout);
    };
  }, [fetchOngoingCalls]);

  // Refresh the Available agents list on every availability broadcast.
  // SetMyAvailability fans out a `Relay("care", { type: "availability_changed",
  // available, ops_uuid })` to every online ops user, so every Hridaya tab
  // gets pushed within a few hundred ms of any toggle — replaces the old
  // 60 s poll.
  useEffect(() => {
    const onAvailabilityChanged = () => fetchAvailableAgents();
    window.addEventListener('twilio-agent-availability-changed', onAvailabilityChanged);
    return () =>
      window.removeEventListener('twilio-agent-availability-changed', onAvailabilityChanged);
  }, [fetchAvailableAgents]);

  // Relays that arrive while the chat socket is down or the tab is hidden are
  // never replayed, which is why this panel could disagree between agents.
  // Resync on the reconnect edge and on tab-visible instead of polling.
  useResyncOnReconnect(fetchAvailableAgents);

  // Refresh the calls-history page when a call finishes. Backend broadcasts
  // `recent_call_finished` to "care" every time a call hits a terminal state
  // (completed, voicemail, missed/canceled, busy, failed, no-answer);
  // home/index.jsx re-emits it as a window event carrying the row.
  //
  // This used to splice the row straight into the table. That is no longer
  // correct: with server-side filters and paging, only the server can say
  // whether the new call matches the active filters, which page it belongs on,
  // and what the new total is — a local splice would show rows the filter
  // excludes and leave "X–Y of Z" stale. So refetch instead, and only when the
  // user is on page 1 newest-first, where a new call actually belongs. On any
  // other page the row would push everything down mid-read.
  useEffect(() => {
    const onUpdate = (e) => {
      const call = e?.detail?.call;
      if (!call || !call.id) return;
      if (page !== 1 || sortDir !== 'desc') return;
      const created = new Date(call.created_at).getTime();
      const fromMs = new Date(fromDate).getTime();
      const toMs = new Date(toDate).getTime() + 24 * 60 * 60 * 1000;
      if (Number.isFinite(created) && (created < fromMs || created >= toMs)) return;
      fetchRecentCalls();
    };
    window.addEventListener('twilio-recent-calls-update', onUpdate);
    return () => window.removeEventListener('twilio-recent-calls-update', onUpdate);
  }, [fromDate, toDate, page, sortDir, fetchRecentCalls]);

  return (
    <div className="p-6">
      <style>{`
        @keyframes twilio-agent-vibrate {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-15deg); }
          40% { transform: rotate(15deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(10deg); }
        }
        .twilio-agent-vibrate-btn svg {
          animation: twilio-agent-vibrate 0.8s infinite;
        }
      `}</style>
      {!notificationBannerDismissed &&
        notificationPermission !== 'granted' &&
        notificationPermission !== 'unsupported' && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              background:
                notificationPermission === 'denied'
                  ? 'var(--state-error-bg)'
                  : 'var(--state-warning-bg)',
              borderColor:
                notificationPermission === 'denied' ? 'var(--state-error)' : 'var(--state-warning)',
              color:
                notificationPermission === 'denied'
                  ? 'var(--state-error-text)'
                  : 'var(--state-warning-text)'
            }}
          >
            <div style={{ fontSize: 13 }}>
              {notificationPermission === 'default'
                ? 'Enable browser notifications so you don’t miss incoming calls.'
                : 'Browser notifications are blocked. Re-enable them in your browser site settings.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {notificationPermission === 'default' && (
                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content="Allow browser notifications so you can be alerted when a call comes in"
                  style={{
                    background: 'var(--brand-primary)',
                    color: 'var(--text-inverse)',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Enable notifications
                </button>
              )}
              <button
                type="button"
                onClick={() => setNotificationBannerDismissed(true)}
                data-tooltip-id="my-tooltip"
                data-tooltip-content="Hide this banner for the current session"
                style={{
                  background: 'transparent',
                  color: 'inherit',
                  border: '1px solid currentColor',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Twilio Agent</h1>
          <button
            type="button"
            onClick={fetchAll}
            disabled={resyncing}
            aria-label="Refresh"
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Refresh ongoing calls and available agents"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              width: 36,
              borderRadius: 9999,
              border: '1px solid var(--border-default)',
              background: 'var(--surface-card)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: resyncing ? 'wait' : 'pointer',
              opacity: resyncing ? 0.7 : 1,
              padding: 0
            }}
          >
            <FaSyncAlt
              size={16}
              color="var(--state-warning)"
              className={resyncing ? 'animate-spin' : ''}
            />
          </button>
          <button
            type="button"
            onClick={() => window.open('/twilio-agent/blocked-numbers', '_blank', 'noopener')}
            aria-label="Blocked numbers"
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Numbers whose calls we decline"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              width: 36,
              borderRadius: 9999,
              border: '1px solid var(--border-default)',
              background: 'var(--surface-card)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <FaBan size={16} color="var(--state-error-text)" />
          </button>
          <button
            type="button"
            onClick={() => setDialerOpen(true)}
            aria-label="Open dialer"
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Open the dialer to place an outgoing call"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              width: 36,
              borderRadius: 9999,
              border: '1px solid var(--border-default)',
              background: 'var(--surface-card)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <MdDialpad size={18} color="var(--state-success)" />
          </button>
          {isSuperUser && (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Twilio settings"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Manage teams and phone routing"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                width: 36,
                borderRadius: 9999,
                border: '1px solid var(--border-default)',
                background: 'var(--surface-card)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <FaCog size={16} color="var(--text-secondary)" />
            </button>
          )}
        </div>
      </div>
      <DialerModal
        show={dialerOpen}
        onClose={() => setDialerOpen(false)}
        onCall={handleDialerCall}
      />
      {settingsOpen && isSuperUser && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      <div className="grid grid-cols-12 gap-6">
        <section className="col-span-12 lg:col-span-8 bg-surface-card rounded-lg shadow-sm border border-line">
          <div className="px-5 py-3 border-b border-line flex items-center justify-between">
            <h2 className="font-semibold">Ongoing calls</h2>
            <span className="text-xs text-content-muted">{ongoingCalls.length} active</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-sunken text-content-secondary">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">Student</th>
                  <th className="text-left px-5 py-2 font-medium">Care agent</th>
                  <th className="text-left px-5 py-2 font-medium">Duration</th>
                  <th className="text-left px-5 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-content-muted">
                      Loading...
                    </td>
                  </tr>
                ) : ongoingCalls.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-6 text-center text-content-muted">
                      No ongoing calls
                    </td>
                  </tr>
                ) : (
                  ongoingCalls.map((call) => {
                    const direction = (call.direction || '').toLowerCase();
                    const isIncomingRinging = direction === 'incoming' && !call.answered;
                    // The student side of the call: `from_number` for
                    // incoming, but `to_number` for outgoing — on an
                    // outgoing call `from_number` is OUR Twilio caller ID,
                    // not the person we dialed.
                    const isOutgoing =
                      direction.startsWith('outgoing') || direction.startsWith('outbound');
                    const studentNumber = isOutgoing
                      ? call.to_number || call.from_number
                      : call.from_number;
                    const callerLabel =
                      call.student_first_name ||
                      studentNumber ||
                      (isOutgoing ? 'Outgoing call' : 'Incoming caller');
                    const agentLabel = call.care_agent_name || call.assigned_agent_name || '--';
                    return (
                      <tr key={call.uuid} className="border-t border-line">
                        <td className="text-left px-5 py-3">
                          {call.student_uuid ? (
                            <Link
                              to={`/studentprofile?id=${encodeURIComponent(call.student_uuid)}`}
                              className="text-blue-600 hover:underline"
                            >
                              {callerLabel}
                            </Link>
                          ) : (
                            callerLabel
                          )}
                        </td>
                        <td className="text-left px-5 py-3">{agentLabel}</td>
                        <td className="text-left px-5 py-3 font-mono text-content-primary">
                          {formatDuration(call.started_at, now)}
                        </td>
                        <td className="text-left px-5 py-3">
                          {(() => {
                            const isUnlocked = !!call.conference_sid;
                            const liveParticipants = participantsByCallUUID[call.uuid] || [];
                            // "self in call" comes from EITHER the live
                            // participant_changed relay (real-time, accurate
                            // after every join/leave) OR the server-derived
                            // your_active_call_uuid (initial-load truth and
                            // cross-tab signal). Both feed in: the relay
                            // catches the leave moment immediately; the
                            // server flag covers the cold-start case where
                            // the relay history isn't replayed for newly-
                            // mounted tabs.
                            const selfInCall =
                              liveParticipants.includes(`cms_${currentUserUUID}`) ||
                              call.uuid === yourActiveCallUUID;
                            // "Active elsewhere" is the cross-tab + cross-row
                            // version: any other ongoing call the server says
                            // I'm in, OR any call active in this tab. Either
                            // disables Join with the "finish current call"
                            // hint.
                            const inOtherServerCall =
                              !!yourActiveCallUUID && yourActiveCallUUID !== call.uuid;
                            const otherCallActive =
                              inOtherServerCall ||
                              (typeof window !== 'undefined' &&
                                (window.__twilioIncomingActive || window.__twilioOutboundActive));

                            if (isIncomingRinging) {
                              return (
                                <span
                                  aria-label="Incoming call ringing"
                                  title="Incoming call ringing — the pill will open automatically when Twilio reaches your browser"
                                  className="twilio-agent-vibrate-btn"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 34,
                                    height: 34,
                                    borderRadius: 9999,
                                    background: 'var(--state-warning-bg)',
                                    border: '1px solid var(--state-warning)',
                                    color: 'var(--state-warning-text)'
                                  }}
                                >
                                  <span
                                    style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}
                                  >
                                    <FaPhone size={14} />
                                  </span>
                                </span>
                              );
                            }

                            // Once the call is unlocked into a conference, anyone NOT
                            // currently in the room sees a Join icon — including the
                            // originator if they previously left. The originator is
                            // first-class allowed to rejoin via the same Join button
                            // as other ops users; the only gate is `selfInCall`,
                            // which the relay + local-clear effect keep accurate.
                            if (isUnlocked && !selfInCall) {
                              const disabled = otherCallActive;
                              return (
                                <button
                                  type="button"
                                  onClick={() => !disabled && joinOngoing(call.uuid)}
                                  disabled={disabled}
                                  aria-label="Join ongoing call"
                                  data-tooltip-id="my-tooltip"
                                  data-tooltip-content={
                                    disabled
                                      ? 'Finish your current call before joining another'
                                      : 'Join this call'
                                  }
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 34,
                                    height: 34,
                                    borderRadius: 9999,
                                    background: disabled
                                      ? 'var(--surface-sunken)'
                                      : 'var(--state-success-bg)',
                                    border: `1px solid ${disabled ? 'var(--border-default)' : 'var(--state-success)'}`,
                                    color: disabled
                                      ? 'var(--text-muted)'
                                      : 'var(--state-success-text)',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    padding: 0
                                  }}
                                >
                                  <BsFillTelephonePlusFill size={14} />
                                </button>
                              );
                            }

                            return <span className="text-content-muted text-xs">—</span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-4 bg-surface-card rounded-lg shadow-sm border border-line">
          <div className="px-5 py-3 border-b border-line flex items-center justify-between">
            <h2 className="font-semibold">Available agents</h2>
            <span className="text-xs text-content-muted">{availableAgents.length}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {loading ? (
              <li className="px-5 py-6 text-center text-content-muted">Loading...</li>
            ) : availableAgents.length === 0 ? (
              <li className="px-5 py-6 text-center text-content-muted">No agents available</li>
            ) : (
              availableAgents.map((agent) => {
                const isSelf = agent.uuid === currentUserUUID;
                return (
                  <li key={agent.uuid} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                      <span className="text-sm text-content-primary">
                        {agent.name || agent.email || agent.uuid}
                      </span>
                    </div>
                    {isSelf && <span className="text-xs text-orange-600 font-medium">(You)</span>}
                  </li>
                );
              })
            )}
          </ul>
        </aside>
      </div>
      <section className="mt-6 bg-surface-card rounded-lg shadow-sm border border-line">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold">Recent calls</h2>
            <span className="text-xs text-content-muted">{resultCountLabel}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fetchRecentCalls()}
              disabled={recentLoading}
              aria-label="Refresh recent calls"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Refresh the recent calls table for the selected date range"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 32,
                width: 32,
                borderRadius: 9999,
                border: '1px solid var(--border-default)',
                background: 'var(--surface-card)',
                cursor: recentLoading ? 'wait' : 'pointer',
                opacity: recentLoading ? 0.7 : 1,
                padding: 0
              }}
            >
              <FaSyncAlt
                size={14}
                color="var(--state-warning)"
                className={recentLoading ? 'animate-spin' : ''}
              />
            </button>
          </div>
        </div>
        <RecentCallsFilters
          memberStatus={memberStatusFilter}
          onMemberStatusChange={changeMemberStatus}
          dateFrom={fromDate}
          dateTo={toDate}
          onDateChange={changeDateRange}
          agentOptions={agentSelectOptions}
          selectedAgents={agentFilter}
          onAgentsChange={changeAgents}
          statusOptions={statusSelectOptions}
          selectedStatuses={callStatusFilter}
          onStatusesChange={changeStatuses}
          direction={directionFilter}
          onDirectionChange={changeDirection}
          activeFilterCount={activeFilterCount}
          onClearAll={clearFilters}
        />
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-sunken text-content-secondary">
              <tr>
                <th
                  onClick={toggleSort}
                  className="text-left px-5 py-2 font-medium cursor-pointer select-none"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={`Sort by date — currently ${sortDir === 'asc' ? 'oldest first' : 'newest first'}. Click to flip.`}
                >
                  Date &amp; time
                  {sortDir === 'asc' ? (
                    <FaArrowUp
                      size={11}
                      color="var(--state-warning)"
                      style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }}
                    />
                  ) : (
                    <FaArrowDown
                      size={11}
                      color="var(--state-warning)"
                      style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }}
                    />
                  )}
                </th>
                <th className="text-left px-5 py-2 font-medium">Student</th>
                <th className="text-left px-5 py-2 font-medium">Care agent</th>
                <th className="text-left px-5 py-2 font-medium">Status</th>
                <th className="text-left px-5 py-2 font-medium">Duration</th>
                <th className="text-left px-5 py-2 font-medium">Direction</th>
                <th className="text-left px-5 py-2 font-medium">Recording</th>
              </tr>
            </thead>
            <tbody>
              {recentLoading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-content-muted">
                    Loading...
                  </td>
                </tr>
              ) : recentError ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-red-600">
                    {recentError}
                  </td>
                </tr>
              ) : recentCalls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-content-muted">
                    {activeFilterCount > 0
                      ? 'No calls match the selected filters'
                      : 'No calls in this range'}
                  </td>
                </tr>
              ) : (
                recentCalls.map((call) => {
                  const dir = (call.direction || 'outgoing').toLowerCase();
                  const dirLabel = dir === 'incoming' ? 'IN' : 'OUT';
                  return (
                    <tr key={call.id} className="border-t border-line">
                      <td className="text-left px-5 py-3 whitespace-nowrap">
                        {moment(call.created_at).format('lll')}
                      </td>
                      <td className="text-left px-5 py-3">
                        {(() => {
                          const badge = studentStatusBadge(call.student_status);
                          const nameNode = call.student_uuid ? (
                            <Link
                              to={`/studentprofile?id=${encodeURIComponent(call.student_uuid)}`}
                              className="text-blue-600 hover:underline"
                            >
                              {call.student_first_name || call.student_uuid}
                            </Link>
                          ) : (
                            call.student_first_name ||
                            // Outgoing: `from_number` is our own Twilio
                            // caller ID, so show the number we dialed.
                            (dir === 'incoming'
                              ? call.from_number
                              : call.to_number || call.from_number) ||
                            '—'
                          );
                          if (!badge) return nameNode;
                          const { Icon, color, label } = badge;
                          return (
                            <span className="inline-flex items-center gap-1.5">
                              <Icon
                                size={20}
                                color={color}
                                data-tooltip-id="my-tooltip"
                                data-tooltip-content={label}
                                aria-label={label}
                              />
                              {nameNode}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="text-left px-5 py-3">{call.care_agent_name || '—'}</td>
                      <td className="text-left px-5 py-3">
                        {call.call_type === 'voicemail' ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 9999,
                              fontSize: 11,
                              fontWeight: 500,
                              color: 'var(--state-info-text)',
                              background: 'var(--state-info-bg)'
                            }}
                          >
                            Voicemail
                          </span>
                        ) : (
                          <StatusPill
                            status={call.status}
                            answered={!!call.answered}
                            answeredBy={call.answered_by}
                          />
                        )}
                      </td>
                      <td className="text-left px-5 py-3 font-mono text-content-primary">
                        {formatSeconds(call.duration)}
                      </td>
                      <td className="text-left px-5 py-3 font-semibold text-content-secondary">
                        {dirLabel}
                      </td>
                      <td className="text-left px-5 py-3">
                        {call.recording_s3_key ? (
                          <AudioPlayer callUuid={call.uuid} />
                        ) : (
                          <span className="text-content-muted text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-line flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs text-content-muted">{resultCountLabel}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || recentLoading}
                style={pagerButtonStyle(page <= 1 || recentLoading)}
              >
                Prev
              </button>
              {/* The shared Paginator's .pgn-box carries a legacy
                  `margin-top: 10px` meant for stacking under a table; cancel
                  it so the numbers sit on the same baseline as Prev/Next. */}
              <div style={{ marginTop: -10 }}>
                <Paginator curr_page={page} page_limit={totalPages} pageChange={setPage} />
              </div>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || recentLoading}
                style={pagerButtonStyle(page >= totalPages || recentLoading)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default TwilioAgent;
